import type { ClientType } from '../types/api';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Users, GraduationCap, Building2 } from 'lucide-react';
import { trackCtaClick } from '../lib/analytics';

export interface AudienceContent {
  type: ClientType;
  icon: React.ComponentType<{ className?: string }>;
}

export const AUDIENCE_CONTENT: Record<ClientType, AudienceContent> = {
  individual: {
    type: 'individual',
    icon: Users,
  },
  student: {
    type: 'student',
    icon: GraduationCap,
  },
  organization: {
    type: 'organization',
    icon: Building2,
  },
};

export function AudienceContentSection({ type }: { type: ClientType }) {
  const { t } = useTranslation('audience');
  const content = AUDIENCE_CONTENT[type];
  const Icon = content.icon;

  return (
    <div className="glass-card rounded-2xl p-8 md:p-12">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-neon-cyan/10 flex items-center justify-center">
          <Icon className="w-7 h-7 text-neon-cyan" />
        </div>
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-text-primary font-heading">{t(`${type}.title`)}</h2>
        </div>
      </div>

      <p className="text-lg text-text-secondary mb-8 leading-relaxed">{t(`${type}.description`)}</p>

      {/* Benefits */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-text-primary mb-4 font-heading">{t('section.whatWeOffer')}</h3>
        <ul className="space-y-3">
          {(t(`${type}.benefits`, { returnObjects: true }) as string[]).map((benefit, index) => (
            <li key={index} className="flex items-start gap-3">
              <svg className="flex-shrink-0 w-5 h-5 text-neon-cyan mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-text-primary">{benefit}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* CTA */}
      <Link
        to="/contact"
        onClick={() => trackCtaClick(`audience_${type}`)}
        className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-neon-cyan to-neon-purple text-white font-semibold rounded-xl hover:shadow-glow-cyan transition-all shadow-lg"
      >
        {t(`${type}.cta`)}
      </Link>
    </div>
  );
}
